// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { ClinicUserType } from "../../types";
import { addUser } from "../../api";

export const useAddClinicUser = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (userData: ClinicUserType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addUser(token, clinicId, userData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate users query cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-users", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("User added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("User could not be added!"),
      });
    },
  });
};
